import { useAuthStore } from '@/shared/stores/auth-store';
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  type MessagesPage,
  type TicketsOrder,
  type TicketsScope,
  getTicket,
  listClosingReasons,
  listMessages,
  listQuickReplies,
  listTags,
  listTickets,
  markTicketRead,
} from './api';
import { aplicaEventoDeStatus } from './aplica-status-do-read';
import { nomeDoUsuario } from './aviso-de-saida';
import { encaixaCardDoInbound } from './card-do-evento';
import { descartarCopiaVelhaDaConversa, registrarCartaoRefletido } from './copia-da-conversa';
import { type InboundEvent, aplicaMensagem } from './encaixe-de-mensagem';
import { escreveHeaderDoInbound } from './header-do-evento';
import {
  applyTicketCard,
  debugInvalidate,
  inboxListeners,
  patchTicket,
  recordEncaixe,
  trocaFotoDoContato,
} from './inbox-debug';
import { marcarMotivo, motivoDaBusca } from './motivo-da-busca';
import { getSocket } from './socket';
import type { TicketDetails, TicketStatus, TicketSummary } from './types';

export function useTicketsList(
  status: TicketStatus,
  scope: TicketsScope = 'mine',
  userId: number | null = null,
  departmentId: number | null = null,
  q = '',
  onlyUnread = false,
  onlyUnanswered = false,
  channelId: number | null = null,
  order: TicketsOrder = 'atividade',
  /** false = ainda não busca (a lista espera a ordem escolhida chegar). */
  enabled = true,
) {
  const qTrim = q.trim();
  const qc = useQueryClient();
  // TEMPORÁRIO (motivo-da-busca.ts): declarado ANTES do useInfiniteQuery de
  // propósito — os efeitos rodam na ordem em que aparecem, então a busca que a
  // lista faz ao montar já encontra esta marca.
  useEffect(() => {
    marcarMotivo('abertura-da-lista');
  }, []);
  return useInfiniteQuery({
    enabled,
    // q vira parte do queryKey → cada busca tem seu próprio cache + refetcha
    // quando o texto muda. Sem o trim e o lower, "abc " e "ABC" virariam
    // queries diferentes desnecessariamente. Os filtros de triagem também
    // entram no key pra refetchar quando ligam/desligam.
    queryKey: [
      'tickets',
      status,
      scope,
      userId,
      departmentId,
      qTrim.toLowerCase(),
      onlyUnread,
      onlyUnanswered,
      // Canal e ordem no FIM da chave: o motor (inbox-debug `parseListKey`) lê
      // as posições — acrescentar no fim não desloca as que já existiam.
      channelId,
      order,
    ],
    queryFn: ({ pageParam, queryKey }) => {
      // TEMPORÁRIO (motivo-da-busca.ts). `fetchMore` no estado = página pedida
      // pela rolagem (fetchNextPage); sem ele, página 2+ é continuação de recarga.
      const estado = qc.getQueryState(queryKey);
      const motivo = motivoDaBusca(JSON.stringify(queryKey), {
        temCursor: typeof pageParam === 'string',
        rolagem: Boolean((estado?.fetchMeta as { fetchMore?: unknown } | null)?.fetchMore),
        semDados: !estado || estado.dataUpdatedAt === 0,
        comBusca: qTrim !== '',
      });
      return listTickets(status, {
        limit: 50,
        scope,
        userId,
        departmentId,
        channelId,
        order,
        motivo,
        // O total só alimenta o contador das abas Ativos/Pendentes, e só o da
        // 1ª página. Fechados não tem contador: a contagem do histórico inteiro
        // ia pro lixo a cada F5 e a cada página guardada.
        semTotal: status === 'closed' || typeof pageParam === 'string',
        ...(qTrim && { q: qTrim }),
        ...(onlyUnread && { onlyUnread: true }),
        ...(onlyUnanswered && { onlyUnanswered: true }),
        ...(typeof pageParam === 'string' && { cursor: pageParam }),
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 5_000,
    /**
     * Voltar pro navegador NÃO recarrega as 3 listas.
     *
     * Quem mantém a inbox em dia é o realtime (evento novo → encaixe ou
     * invalidação pontual) e o serviço de reconexão (`reconexao.ts`), que ao
     * voltar pergunta o que mudou e corrige só aquilo. A recarga por foco era
     * redundante nisso.
     *
     * Cheguei a defendê-la como rede de segurança pro caso de o realtime
     * parar de avisar em silêncio (incidente 17/jul, adapter Socket.IO
     * zumbi). Mas ela já NÃO cobria o caso comum: a telemetria mediu
     * `aba_voltou: 6` contra `app_voltou: 0` — o TanStack ouve
     * `visibilitychange`, que não dispara ao alternar entre APLICATIVOS
     * (Chrome ↔ WhatsApp), só ao trocar de aba dentro do navegador.
     * Ou seja: proteção que existia só em metade das saídas, e que naquele
     * incidente não impediu nada (durou horas; resolveu recriando o
     * container). Alarme server-side é o lugar certo pra isso.
     */
    refetchOnWindowFocus: false,
    // Mantém a página anterior visível durante refetch (mudança de q, scope,
    // userId). Sem isso, ao digitar na busca, a lista pisca: `data` vira
    // undefined entre queries → `list = []` → renderiza "Nenhum ticket" →
    // resultado chega → re-renderiza. Com `keepPreviousData`, a lista
    // antiga continua na tela até a nova chegar — flicker zero.
    placeholderData: keepPreviousData,
  });
}

/**
 * Cabeçalho da conversa (nome, foto, protocolo, etiquetas e o `last_inbound_at`
 * que decide a janela de 24h).
 *
 * NÃO vence por relógio. O padrão global de 30s fazia clicar num card rebuscar
 * o cabeçalho mesmo sem nada ter mudado — e "mudou" é coisa que o painel FICA
 * SABENDO: mensagem do contato invalida este cache (onInboundTicket), ação no
 * ticket idem (onTicketChanged), e a reconexão marca os cabeçalhos que o delta
 * apontou como mudados. O relógio era rede de segurança de quando não se
 * confiava no realtime;
 * virou desperdício depois do motor. Mesma decisão dos cadastros do tenant
 * (CADASTROS_STALE_MS) e pelo mesmo motivo.
 */
export function useTicket(ticketId: number | null) {
  return useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => getTicket(ticketId as number),
    enabled: ticketId !== null,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Busca na conversa: até onde a conversa precisa descer, por conversa (epoch
 * micros da mensagem achada mais antiga). Enquanto houver alvo, o bloco que
 * começa ACIMA dele desce de uma vez até ele (`ate`) em vez de vir em 20.
 *
 * Fica guardado (e não só no salto) por causa da rebusca: o TanStack rebusca
 * o mesmo NÚMERO de blocos, com 20 cada — sem o alvo, a conversa encolheria
 * de volta e a mensagem achada sumiria da tela no meio da leitura. Com ele, a
 * rebusca desce até o mesmo ponto. Esquecido ao sair da conversa (que também
 * descarta o histórico rolado — vide ThreadPanel).
 */
const alvoDaBusca = new Map<number, number>();

export function marcarAlvoDaBusca(ticketId: number, micros: number): void {
  const atual = alvoDaBusca.get(ticketId);
  if (atual === undefined || micros < atual) alvoDaBusca.set(ticketId, micros);
}

export function esquecerAlvoDaBusca(ticketId: number): void {
  alvoDaBusca.delete(ticketId);
}

/**
 * Carrega mensagens em blocos de 20, do mais recente pro mais antigo.
 * `pages[0]` = batch mais novo (sem cursor); `pages[N]` = batches anteriores (cursor=before).
 * Render flatten: reverter pages e concatenar `messages` (cada page já está em ordem cronológica).
 */
export function useTicketMessages(ticketId: number | null, enabled = true) {
  /**
   * Rede de segurança na ABERTURA: cópia atrás do cartão da lista é descartada
   * antes de a conversa se inscrever, e ela abre em "carregando" em vez de
   * mostrar o buraco (vide copia-da-conversa.ts). Uma vez por abertura: o ref
   * guarda qual conversa já foi conferida, então re-renders da mesma conversa
   * não conferem de novo — e o mesmo valor de cartão nunca rebusca duas vezes.
   *
   * Roda no render, ANTES do `useInfiniteQuery`, de propósito: é o ponto em que
   * a cópia ainda não chegou à tela. Num efeito, a conversa velha já teria
   * aparecido por um quadro. É idempotente (a segunda passada do StrictMode não
   * acha mais cópia) e só remove entrada sem observador.
   */
  const qc = useQueryClient();
  const conferidaRef = useRef<number | null>(null);
  // Painel vazio (conversa fechou sozinha, foi assumida por outro…): a próxima
  // abertura, mesmo que da MESMA conversa, é uma abertura nova.
  if (ticketId === null) conferidaRef.current = null;
  if (ticketId !== null && conferidaRef.current !== ticketId) {
    conferidaRef.current = ticketId;
    descartarCopiaVelhaDaConversa(qc, ticketId);
  }

  const consulta = useInfiniteQuery({
    queryKey: ['ticket-messages', ticketId],
    queryFn: ({ pageParam }) => {
      const alvo = ticketId === null ? undefined : alvoDaBusca.get(ticketId);
      return listMessages(ticketId as number, {
        limit: 20,
        ...(typeof pageParam === 'number' && { before: pageParam }),
        ...(typeof pageParam === 'number' &&
          alvo !== undefined &&
          alvo < pageParam && { ate: alvo }),
      });
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    // Fila cega: quando a conversa está bloqueada (pendente + canal oculto +
    // não-admin), NÃO busca (o servidor retornaria 403) — mostra placeholder.
    enabled: ticketId !== null && enabled,
    /**
     * A conversa NÃO vence por relógio — quem manda é o evento.
     *
     * Toda mensagem nova é encaixada aqui pelo `onInboundTicket` (aberta OU
     * fechada), então o cache reflete a verdade sem precisar rebuscar. Com o
     * padrão de 30s, clicar num card "pra ver se o cliente respondeu" baixava
     * 20 mensagens pra descobrir que nada mudou — medido no srv01: 60% das
     * buscas de conversa eram recarga do MESMO ticket em menos de 60s.
     *
     * Quando o encaixe não é possível (conversa nunca aberta nesta sessão), o
     * próprio handler invalida.
     *
     * A rede de segurança pro que se perde FORA DO AR é o serviço de reconexão
     * (`reconexao.ts`): ao voltar, ele pergunta o que mudou e marca só essas
     * conversas — mais a que estiver aberta, que confere sempre porque
     * confirmação de entrega mexe na mensagem e não no ticket. Antes ele
     * marcava TODAS, e era isso que fazia abrir a mesma conversa pela terceira
     * vez custar três buscas.
     */
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Conversa na tela com dados (busca ou atualização ao vivo): a cópia reflete
  // o cartão atual — a conferência da abertura só volta a valer quando o
  // cartão mudar (vide registrarCartaoRefletido).
  const dadosEm = consulta.dataUpdatedAt;
  useEffect(() => {
    if (ticketId !== null && dadosEm > 0) registrarCartaoRefletido(qc, ticketId);
  }, [qc, ticketId, dadosEm]);

  return consulta;
}

/**
 * Cadastros do tenant (respostas rápidas, motivos de fechamento, etiquetas).
 *
 * Validade de 10min, não 1min. São listas que o admin mexe raramente — uma
 * etiqueta nova por semana, no máximo — mas os componentes que as consomem
 * remontam o tempo todo: `useQuickReplies` vive no Composer, que remonta a
 * CADA conversa aberta. Com validade de 1min, trocar de conversa depois desse
 * intervalo re-buscava tudo de novo. Medido no srv01: ~3.250 requisições/hora
 * só nas três, com 21 atendentes.
 *
 * Quem edita continua vendo na hora: as telas de Configurações invalidam a
 * lista correspondente no sucesso da própria alteração. O atraso de até 10min
 * é só para os OUTROS atendentes verem um cadastro recém-criado — aceitável
 * pra dado que muda uma vez por semana.
 */
/**
 * Cadastros do tenant (mensagens rápidas, etiquetas, motivos) NÃO vencem por
 * relógio — buscam quando o atendente ABRE a lista.
 *
 * Antes era prazo fixo: 1min, e eu subi pra 10min pra economizar requisição.
 * Trocou pouca coisa em carga e quebrou o fluxo real — o admin criava uma
 * mensagem rápida e o atendente só via minutos depois. O prazo é ruim nos dois
 * sentidos: curto demais gasta à toa, longo demais mostra dado velho.
 *
 * Buscar na abertura acerta os dois: quem não abre não gasta nada, e quem abre
 * vê o estado atual. `Infinity` desliga o vencimento por tempo; o refetch vem
 * de `useRefetchOnOpen` (ou de `refetchOnMount` nos que montam ao abrir).
 */
const CADASTROS_STALE_MS = Number.POSITIVE_INFINITY;

export function useQuickReplies() {
  return useQuery({
    queryKey: ['quick-replies'],
    queryFn: listQuickReplies,
    staleTime: CADASTROS_STALE_MS,
  });
}

export function useClosingReasons() {
  return useQuery({
    queryKey: ['closing-reasons'],
    queryFn: listClosingReasons,
    staleTime: CADASTROS_STALE_MS,
    // NÃO usar `refetchOnMount: 'always'` aqui. Os modais de fechar recebem
    // `open` como prop e ficam SEMPRE montados (ThreadPanel:1119,
    // InboxPanel:518) — o `if (!open) return null` vem DEPOIS do hook. Com
    // 'always' isso dispararia a cada remontagem do ThreadPanel, ou seja, a
    // cada troca de conversa (foi o bug das preferências, commit 11bfafe).
    // Quem rebusca é `useRefetchOnOpen(open, ...)` dentro dos modais.
  });
}

export function useTagsList() {
  return useQuery({ queryKey: ['tags'], queryFn: listTags, staleTime: CADASTROS_STALE_MS });
}

/** Payload dos eventos de ciclo de vida (queued/closed/reopened/assigned/
 *  transferred/tagged/chatbot.ended). Desde a Fase 1 do motor, o servidor
 *  inclui o CARTÃO COMPLETO (`ticket`; `tickets` nos bulks) — o painel
 *  encaixa em vez de refetchar. Campos opcionais: eventos antigos (deploy
 *  desencontrado) e ticket.tagged/chatbot.ended não carregam cartão. */
interface TicketLifecycleEvent {
  ticket_id?: number;
  ticket_ids?: number[];
  /** Quem fez a ação. null/ausente = automação (chatbot, fechamento por inatividade). */
  actor_user_id?: number | null;
  to_user_id?: number | null;
  ticket?: TicketSummary;
  tickets?: TicketSummary[];
  /** 'atendente_offline' = devolvido pra Pendentes pela vigia (traz faixa nova). */
  automatico?: string;
}

interface MediaReadyEvent {
  tenant_id: number;
  ticket_id: number;
  message_id: number;
  // Nomes "novos" emitidos por worker-media/convert-audio.ts:
  media_id?: number;
  status?: 'stored' | 'failed';
  media_mime?: string;
  media_waveform?: number[];
  // Aliases legados (whatsmeow inline download, fluxo antigo):
  media_file_id?: number;
  mime_type?: string;
  waveform?: number[];
}

interface StatusEvent {
  tenant_id: number;
  ticket_id: number;
  // Pelo menos um dos dois vem; preferimos message_id (sempre disponível,
  // bate com optimistic outbound que ainda tem wamid=null).
  message_id?: number;
  wamid?: string;
  status: 'sent' | 'delivered' | 'read' | 'failed';
  status_rank: number;
  timestamp: string;
  // Quando status='failed', workers preenchem pra alimentar tooltip da bolha.
  error_code?: string;
  error_title?: string;
}

/**
 * Story 3.12 — assina rooms da inbox + ticket aberto e atualiza queries do TanStack.
 *
 * - `message.received` na room da inbox → invalida lista de tickets do status correspondente
 * - `message.received` no ticket aberto → push da bolha pra cache de mensagens
 * - `message.status.updated` → patch otimista no cache de mensagens
 * - `ticket.queued|transferred|closed|tagged` → invalida lista
 */
export function useRealtimeListeners(
  activeStatus: TicketStatus,
  openTicketId: number | null,
  onTicketTakenAway?: () => void,
) {
  const qc = useQueryClient();
  // Ref pra não re-registrar os listeners do socket a cada render só porque o
  // callback mudou de identidade (a callback é chamada de dentro do handler).
  const takenAwayRef = useRef(onTicketTakenAway);
  takenAwayRef.current = onTicketTakenAway;

  useEffect(() => {
    const socket = getSocket();

    socket.emit('joinInbox', 'active');
    socket.emit('joinInbox', 'pending');
    socket.emit('joinInbox', 'closed');
    return () => {
      // NÃO sair das rooms `active` e `pending` — `useDesktopNotifications`
      // (global no AppShell) precisa delas pra tocar som quando o atendente
      // está em outra página. Sai só do `closed` que ninguém mais usa.
      // Re-join é idempotente, então voltar pra atendimento não duplica.
      socket.emit('leaveInbox', 'closed');
    };
  }, []);

  useEffect(() => {
    if (openTicketId === null) return;
    const socket = getSocket();
    socket.emit('joinTicket', openTicketId);

    return () => {
      socket.emit('leaveTicket', openTicketId);
    };
  }, [openTicketId]);

  /**
   * A volta do socket NÃO é tratada aqui — quem cuida é o serviço de
   * reconexão (`reconexao.ts`), instalado junto com o socket, fora do React.
   *
   * Aqui existia um `socket.on('connect')` que re-entrava nas salas e
   * invalidava TUDO (conversas, cabeçalhos, listas). Saiu por dois motivos:
   *
   *   1. Desconfiar de tudo cobra releitura até do que não mudou. O delta
   *      pergunta "o que mudou desde X?" e corrige só aquilo.
   *   2. Viver num `useEffect` desta tela era o furo. `SocketProxy.on()` não
   *      repete evento passado: um `connect` que chegasse antes da montagem
   *      levava a recuperação junto — e, montado, só cobria quem estivesse no
   *      Atendimento. Quem estava no Dashboard quando a linha voltou seguia
   *      com dado velho até um F5. Era a origem de "fechei o notebook, voltei,
   *      minha mensagem não estava lá".
   *
   * ⚠️ É o serviço de reconexão que sustenta o `staleTime: Infinity` da
   * conversa e das listas. Se ele for desligado, o vencimento por relógio tem
   * que voltar — senão o cache persistido de 24h vira verdade eterna.
   */

  useEffect(() => {
    const socket = getSocket();

    const onInboundInbox = (evt: InboundEvent): void => {
      // Motor no eco de mensagem: o evento NÃO carrega cartão (workers não
      // montam TicketSummary), mas se o ticket já está em QUALQUER lista em
      // cache, o cartão de lá + os campos do evento bastam — o
      // `encaixaCardDoInbound` (extraído pra módulo, também usado pelo handler
      // global com a página desmontada) redistribui sem nenhuma requisição.
      if (encaixaCardDoInbound(qc, evt, openTicketId)) return;
      // Ticket em nenhuma lista carregada (novo, ou fora da janela) → só o
      // servidor traz o cartão + total certo. Comportamento de antes.
      const status = evt.ticket_status ?? activeStatus;
      /**
       * Atendimento ATIVO de outra pessoa não está na minha lista e não vai
       * entrar nela: Ativos "meus" só tem os meus. Rebuscar a 1ª página não
       * trazia nada — e acontecia a cada mensagem de atendimento de colega do
       * departamento (medido no srv03, 03/nov: 85 buscas em 22 min, 5
       * painéis). Aí só a lista "Visualizar todos", se existir, rebusca.
       */
      const eu = useAuthStore.getState().user;
      const deColega =
        status === 'active' &&
        typeof evt.user_id === 'number' &&
        eu != null &&
        evt.user_id !== eu.id;
      void debugInvalidate(
        qc,
        ['tickets', status],
        'onInboundInbox',
        'message.received',
        deColega ? (q) => q.queryKey[2] === 'all' : undefined,
      );
    };

    /**
     * Encaixa a mensagem do evento no cache da conversa. Devolve `false` quando
     * não havia cache pra encaixar (conversa nunca aberta nesta sessão) — aí o
     * chamador invalida, que é o único caso em que só o servidor tem o
     * conteúdo.
     *
     * Usado nas DUAS situações: conversa aberta (append ao vivo) e conversa
     * fechada. Antes, a fechada só era marcada como velha e o clique seguinte
     * rebuscava 20 mensagens pra trazer 1 que já tinha chegado no evento.
     */
    const encaixaMensagem = (evt: InboundEvent, ticketId: number): boolean => {
      let encaixou = false;
      qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
        ['ticket-messages', ticketId],
        (prev) => {
          if (!prev || prev.pages.length === 0) return prev;
          encaixou = true;
          return aplicaMensagem(prev, evt);
        },
      );
      return encaixou;
    };

    const onInboundTicket = (evt: InboundEvent): void => {
      if (openTicketId === null || evt.ticket_id !== openTicketId) {
        // Conversa FECHADA: o evento já traz o conteúdo (texto, tipo, dados da
        // mídia) — encaixa direto no cache. Só invalida quando não há cache
        // pra encaixar.
        if (evt.ticket_id) {
          if (evt.direction !== 'outbound') {
            // Inbound muda status e contact.last_inbound_at do header — e esta
            // rota não os tocava (banner 24h/composer presos até F5, medido no
            // rastro 2026-07-24). ESCREVE do evento (abrir a conversa já vem
            // certo, sem flash do banner) e invalida como rede de segurança
            // (query inativa: só marca stale; refetch confirma ao abrir).
            escreveHeaderDoInbound(qc, evt);
            qc.invalidateQueries({ queryKey: ['ticket', evt.ticket_id] });
          }
          const ok = encaixaMensagem(evt, evt.ticket_id);
          if (ok) recordEncaixe('onInboundTicket:fechada', 'message.received');
          if (!ok) {
            void debugInvalidate(
              qc,
              ['ticket-messages', evt.ticket_id],
              'onInboundTicket',
              'message.received',
            );
          } else {
          }
        }
        return;
      }
      // Ticket aberto — zera contador no banco imediatamente. SÓ pra inbound:
      // o eco da PRÓPRIA mensagem enviada não cria não-lido, e o read receipt
      // do canal QR só se aplica a mensagens do contato (medido: 2 POST /read
      // por envio, ambos no-op no servidor).
      if (evt.direction !== 'outbound') void markTicketRead(openTicketId);
      // Invalida cache do ticket pra refetch fresh de `contact.last_inbound_at`.
      // Sem isso, quando cliente responde fora da janela 24h, o composer
      // continua disabled + banner "escolher template" persiste (porque
      // within24h é calculado do valor antigo do cache) até user F5.
      // Só pra direction=inbound — outbound nosso não muda last_inbound_at.
      if (evt.direction !== 'outbound') {
        // Escreve do evento ANTES do invalidate — o banner "escolher template"
        // some no mesmo frame em que a msg entra, sem esperar o refetch (que
        // continua rodando como rede de segurança).
        escreveHeaderDoInbound(qc, evt);
        qc.invalidateQueries({ queryKey: ['ticket', openTicketId] });
      }
      encaixaMensagem(evt, openTicketId);
    };

    const onStatus = (evt: StatusEvent): void => {
      /**
       * Toast de número inválido SÓ pra conversa aberta: com o fan-out do
       * status pro DEPARTAMENTO, avisos de conversas alheias chegam aqui —
       * alertar sobre o envio dos outros seria ruído.
       */
      if (
        evt.status === 'failed' &&
        evt.error_code === 'NUMBER_NOT_ON_WA' &&
        evt.ticket_id === openTicketId
      ) {
        toast.error('Número inválido', {
          description: 'Este número não possui WhatsApp cadastrado.',
        });
      }
      /**
       * Aplica em QUALQUER conversa em cache — não só na aberta.
       *
       * A guarda `evt.ticket_id !== openTicketId → return` que morava aqui era
       * metade do defeito da bolha presa no relógio (a outra metade era o
       * servidor nem mandar o status pra quem estava fora — resolvida no
       * fan-out pro dept, vide realtime/server.ts). Agora o aviso chega e é
       * aplicado no cache fechado: a primeira pintura ao abrir já vem certa,
       * sem o pisca relógio→✓✓.
       *
       * A aplicação é idempotente (duplo recebimento por estar nas duas salas
       * = mesma referência, zero re-render) — vide `aplicaEventoDeStatus`.
       */
      aplicaEventoDeStatus(qc, evt);
    };

    const onTicketChanged = (evt?: TicketLifecycleEvent, eventName = 'ticket.changed'): void => {
      // Motor (Fase 2): evento traz o CARTÃO COMPLETO (`ticket`, ou `tickets`
      // nos bulks) → encaixa direto nas listas em cache, zero refetch. Sem
      // cartão (evento antigo, montagem falhou no servidor, ticket.tagged /
      // chatbot.ended que não carregam) → invalida como sempre. Nunca pior
      // que o comportamento de hoje.
      const cards = evt?.tickets ?? (evt?.ticket ? [evt.ticket] : []);
      const me = useAuthStore.getState().user;
      if (cards.length > 0 && me) {
        const viewer = {
          id: me.id,
          profile: me.profile,
          departmentIds: me.departmentIds,
          support: Boolean(me.impersonating),
        };
        for (const card of cards) {
          applyTicketCard(qc, card, viewer, { origin: 'onTicketChanged', event: eventName });
        }
      } else {
        void debugInvalidate(qc, ['tickets'], 'onTicketChanged', eventName);
      }
      /**
       * Etiqueta não mexe em mensagem — e por isso não paga por conversa.
       *
       * O resto dos eventos daqui muda status (fechado/reaberto/transferido),
       * e status muda o ESCOPO das mensagens (fechado filtra por atendimento;
       * ativo e pendente filtram por contato), ou acrescenta faixa na conversa
       * (transferência). Etiqueta não faz nem um nem outro: é rótulo.
       *
       * Media no rastro: marcar UMA etiqueta custava 7 requisições, e uma delas
       * era rebuscar 20 mensagens que ninguém tocou.
       */
      const soRotulo = eventName === 'ticket.tagged';

      /**
       * A CONVERSA só é rebuscada quando o evento muda o que ela mostra.
       *
       * Aqui havia um comentário dizendo "status mudou → o escopo das mensagens
       * muda (fechado filtra por ticket; ativo/pendente filtra por contato)".
       * Isso deixou de ser verdade na lição #31: hoje a conversa SEMPRE mostra
       * o histórico do contato no canal, qualquer que seja o status. O
       * comentário ficou pra trás e o refetch que ele justificava virou
       * desperdício — 20 mensagens baixadas por fechar um atendimento.
       *
       * O que realmente entra na thread são as faixas de TRANSFERÊNCIA e de
       * REABERTURA (o servidor filtra `eventType IN ('transferred','reopened')`).
       * Fechar, enfileirar e aceitar não acrescentam nada lá.
       */
      // Devolução de quem ficou offline também acrescenta faixa (vigia-offline);
      // o "Fila" manual não.
      const mexeNaConversa =
        eventName === 'ticket.transferred' ||
        eventName === 'ticket.reopened' ||
        (eventName === 'ticket.queued' && evt?.automatico === 'atendente_offline');

      // Invalida cache do ticket específico do evento — cobre o caso do admin
      // que transfere o ticket (ThreadPanel fecha sozinho, openTicketId=null,
      // mas a aba pode ser reaberta com cache stale sem o banner de transfer).
      // Barato: só refetcha se a query estiver ativa (= conversa aberta).
      const ids = evt?.ticket_ids ?? (typeof evt?.ticket_id === 'number' ? [evt.ticket_id] : []);
      for (const id of ids) {
        // O cabeçalho mostra as etiquetas, e o cartão do evento TRAZ as
        // etiquetas — mesmo formato dos dois lados. Então dá pra escrever
        // direto em vez de mandar buscar: a última requisição da etiqueta
        // some, e marcar passa a custar só o próprio POST.
        /**
         * O cabeçalho é ESCRITO a partir do cartão, não buscado.
         *
         * O cartão do evento já traz o que o cabeçalho mostra e que muda nestas
         * ações: status (decide o botão Reabrir), dono, departamento e
         * etiquetas. Escrever em vez de mandar buscar tira a última requisição
         * de fechar um atendimento — que passa a custar só o próprio POST.
         *
         * Sem cartão (evento antigo, montagem falhou no servidor), invalida
         * como sempre: nunca pior que o comportamento de hoje.
         */
        const cartaoDoTicket = cards.find((c) => c.id === id);
        if (cartaoDoTicket) {
          qc.setQueryData(['ticket', id], (antigo: TicketDetails | undefined) =>
            antigo
              ? {
                  ...antigo,
                  status: cartaoDoTicket.status,
                  user_id: cartaoDoTicket.user_id,
                  department_id: cartaoDoTicket.department_id,
                  tags: cartaoDoTicket.tags,
                }
              : antigo,
          );
        } else {
          qc.invalidateQueries({ queryKey: ['ticket', id] });
        }
        if (mexeNaConversa) qc.invalidateQueries({ queryKey: ['ticket-messages', id] });
      }
      if (!soRotulo && openTicketId !== null && !ids.includes(openTicketId)) {
        qc.invalidateQueries({ queryKey: ['ticket', openTicketId] });
        if (mexeNaConversa) qc.invalidateQueries({ queryKey: ['ticket-messages', openTicketId] });
      }
    };

    // Admin mudou config do canal (ex: fila cega hide_pending_content) — refetch
    // tickets/canais pra a máscara refletir no atendente logado SEM F5. Também
    // invalida o ticket aberto (thread pode passar a bloquear/desbloquear).
    const onChannelUpdated = (): void => {
      void debugInvalidate(qc, ['tickets'], 'onChannelUpdated', 'channel.updated');
      // `['channels']` NÃO é invalidado aqui: o ChannelHealthBadge já escuta o
      // mesmo `channel.updated` e faz isso — e ele mora na Topbar, presente em
      // TODA tela autenticada, então cobre mais que este hook (que só existe no
      // Atendimento). Os dois juntos buscavam a lista de canais duas vezes pelo
      // mesmo evento; apareceu no rastro, ao voltar pra aba.
      if (openTicketId !== null) {
        qc.invalidateQueries({ queryKey: ['ticket', openTicketId] });
        qc.invalidateQueries({ queryKey: ['ticket-messages', openTicketId] });
      }
    };

    // ticket.assigned / ticket.transferred — quando o atendimento que EU tenho
    // ABERTO é assumido/transferido pra OUTRA pessoa, fecho a conversa na hora.
    // Espelha o que já acontece na lista de pendentes (some quando alguém aceita)
    // e evita dois atendentes na mesma conversa (casos Wilson/Robertinho).
    // Vale pra admin também — muitos admins atendem normal, então não podem ficar
    // com a conversa aberta depois que outro assumiu. (O backend segue liberando
    // o admin a enviar/transferir; o fechar-auto só dispara quando OUTRO assume.)
    const onAssignedOrTransferred = (
      evt?: TicketLifecycleEvent,
      eventName = 'ticket.assigned',
    ): void => {
      onTicketChanged(evt, eventName);
      // Transferência em LOTE traz `ticket_ids` em vez de `ticket_id` — sem
      // olhar a lista, a conversa aberta de quem perdeu o atendimento seguia
      // na tela, recebendo ao vivo as respostas do novo dono.
      const afetaAberto =
        openTicketId !== null &&
        (evt?.ticket_id === openTicketId || (evt?.ticket_ids?.includes(openTicketId) ?? false));
      if (!afetaAberto) return;
      const me = useAuthStore.getState().user;
      if (!me) return;
      // to_user_id presente e diferente de mim = outra pessoa assumiu.
      if (typeof evt?.to_user_id === 'number' && evt.to_user_id !== me.id) {
        takenAwayRef.current?.();
        toast.info('Este atendimento foi assumido por outro atendente.');
      }
    };

    // Cross-device sync: outro device (mobile, outra aba) abriu o ticket e
    // marcou como lido. Zera badge local sem precisar refetch.
    const onTicketRead = (evt: { ticket_id: number }): void => {
      patchTicket(
        qc,
        evt.ticket_id,
        (t) => (t.unread_count === 0 ? t : { ...t, unread_count: 0 }),
        {
          origin: 'onTicketRead',
          event: 'ticket.read',
        },
      );
    };

    // Alguém (eu em outra aba, ou outro atendente do dept) marcou a conversa
    // como NÃO lida de volta. Acende o selo no card sem rebuscar a lista.
    const onTicketUnread = (evt: { ticket_id: number; unread_count?: number }): void => {
      const conta = evt.unread_count ?? 1;
      patchTicket(qc, evt.ticket_id, (t) => (t.unread_count > 0 ? t : { ...t, unread_count: conta }), {
        origin: 'onTicketUnread',
        event: 'ticket.unread',
      });
    };

    // ticket.transferred.cleared: alguém do dept abriu o ticket transferido →
    // limpa transferred_at no cache (ícone some). Posição do card NÃO muda
    // porque o sort usa last_message_at (que foi bumpado no momento do
    // transfer pelo backend).
    const onTransferredCleared = (evt: { ticket_id: number }): void => {
      patchTicket(
        qc,
        evt.ticket_id,
        (t) => (t.transferred_at === null ? t : { ...t, transferred_at: null }),
        { origin: 'onTransferredCleared', event: 'ticket.transferred.cleared' },
      );
    };

    // Chatbot começou a atender (worker-chatbot emite ANTES do delay de digitação,
    // JÁ com a execução 'running' criada) → acende o ícone de bot no card na hora.
    // Patch direto na lista; como a execução já existe no DB, refetches confirmam
    // (não sobrescrevem pra false). Se o ticket ainda não está em nenhuma lista
    // carregada, o patch é no-op e o ícone aparece no refetch do inbound (que já
    // lê bot_active=true) — sem regressão.
    const onChatbotStarted = (evt: { ticket_id: number }): void => {
      patchTicket(
        qc,
        evt.ticket_id,
        (t) => (t.bot_active === true ? t : { ...t, bot_active: true }),
        {
          origin: 'onChatbotStarted',
          event: 'chatbot.started',
        },
      );
    };

    /**
     * Fim do fluxo do bot — o espelho exato do início acima.
     *
     * Antes caía no `onTicketChanged`, que sem cartão joga as 3 listas fora,
     * mais o cabeçalho e a conversa inteira. Seis requisições pra apagar um
     * ícone.
     *
     * O cartão não vem porque quem emite é o `worker-chatbot`, e a montagem do
     * cartão vive na API. Mas aqui isso não faz falta: acabar o fluxo muda UM
     * campo — `bot_active` — e o painel já tem o resto do cartão. Remendo local,
     * zero requisição.
     *
     * ⚠️ Se um dia o fim do bot passar a mexer em mais coisa (mudar status,
     * atribuir dono), este remendo fica incompleto e aí sim vale mandar o
     * cartão de verdade — o que exigiria a montagem do cartão virar pacote
     * compartilhado, hoje presa em `apps/api`.
     */
    const onChatbotEndedPatch = (evt?: { ticket_id?: number }): void => {
      if (typeof evt?.ticket_id !== 'number') {
        // Sem id não há o que remendar — cai no caminho antigo.
        void debugInvalidate(qc, ['tickets'], 'onChatbotEnded', 'chatbot.ended');
        return;
      }
      patchTicket(
        qc,
        evt.ticket_id,
        (t) => (t.bot_active === false ? t : { ...t, bot_active: false }),
        { origin: 'onChatbotEnded', event: 'chatbot.ended' },
      );
    };

    // Disparado quando worker-whatsmeow corrige número via "regra do 9" e
    // atualiza display_name + identifier no DB. Frontend invalida queries
    // pra refletir o número correto sem F5.
    // eventName é OBRIGATÓRIO (sem default): este handler NUNCA pode ser
    // registrado direto no socket — o payload do evento cairia no parâmetro
    // (achado da revisão adversarial). Só chamar via wrappers abaixo.
    const onContactUpdated = (eventName: string): void => {
      void debugInvalidate(qc, ['tickets'], 'onContactUpdated', eventName);
      qc.invalidateQueries({ queryKey: ['contacts'] });
      if (openTicketId !== null) {
        qc.invalidateQueries({ queryKey: ['ticket', openTicketId] });
      }
    };

    // Story 4.2 — quando worker-media termina o download, atualiza a bolha existente.
    const onMediaReady = (evt: MediaReadyEvent): void => {
      if (openTicketId === null || evt.ticket_id !== openTicketId) return;
      // Backend hoje emite `media_id`/`status` (worker-media convert-audio).
      // Caminho legado de whatsmeow inline-download usava `media_file_id`/`mime_type`
      // — aceitamos ambos pra retro-compat. Sem o fallback, o handler zerava
      // `media_id` quando o evento vinha com o nome novo (bolha → [audio]).
      const incomingMediaId = evt.media_id ?? evt.media_file_id;
      const incomingStatus = evt.status ?? 'stored';
      const incomingMime = evt.media_mime ?? evt.mime_type;
      const incomingWaveform = evt.media_waveform ?? evt.waveform;
      qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
        ['ticket-messages', openTicketId],
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((p) => ({
              ...p,
              messages: p.messages.map((m) =>
                m.id === evt.message_id
                  ? {
                      ...m,
                      // Só patcha se vier valor — evita ZERAR campos já preenchidos
                      // por um message.received anterior (causa raiz do bug).
                      ...(incomingMediaId !== undefined && { media_id: incomingMediaId }),
                      ...(incomingStatus && { media_status: incomingStatus }),
                      ...(incomingMime !== undefined && { media_mime: incomingMime }),
                      ...(incomingWaveform && { media_waveform: incomingWaveform }),
                    }
                  : m,
              ),
            })),
          };
        },
      );
    };

    // Handler nomeado pra que o cleanup remova SÓ esse listener (não os de outros hooks).
    // O servidor emite `message.received` pra sala do TICKET (thread aberta)
    // E pra sala do DEPARTAMENTO (lista) — e este socket está nas duas, então
    // o mesmo evento chega 2×. Processar em dobro custava caro: 2 refetches
    // de thread + 2 possíveis invalidações de lista + 2 POST /read por
    // mensagem (medido no rastro de um envio). Dedupe por conteúdo: a chave
    // inclui `media_status` porque o worker-media RE-EMITE o mesmo message_id
    // legitimamente quando a mídia termina de baixar (pending → stored,
    // lição #57) — esse segundo emit tem que passar.
    //
    // A CHAVE É O PAYLOAD INTEIRO, não uma lista de campos escolhidos a dedo.
    //
    // A lista era uma armadilha que cobrou três vezes numa sessão só: a mesma
    // mensagem é reemitida ao longo da vida dela pra avisar o que mudou (saiu
    // pelo celular, custou, o pacote fechou), sempre com o mesmo id e o mesmo
    // corpo. Todo aviso novo que mexesse num campo fora da lista era silenciosamente
    // engolido, o painel não mudava, e o defeito só aparecia como "no F5 fica
    // certo" — caro de diagnosticar e fácil de repetir na próxima feature.
    //
    // O payload cobre isso sozinho, e sem perder a proteção original: o evento
    // que chega em dobro (sala do ticket + sala do departamento) é o MESMO
    // objeto entregue duas vezes, então serializa igual e continua sendo
    // descartado. Já um aviso com qualquer conteúdo diferente passa — que é
    // exatamente o comportamento desejado.
    const seenMessages = new Map<string, number>();
    const onMessageReceived = (evt: InboundEvent): void => {
      if (evt.message_id !== null) {
        // A miniatura de resposta a status vem em base64 e pode ter dezenas de
        // KB. Ela nunca muda entre reemissões da mesma mensagem, então entra na
        // chave só pelo tamanho — o cache guarda 200 chaves e não vale carregar
        // imagem inteira em cada uma.
        const key = JSON.stringify(evt, (k, v) =>
          k === 'status_thumbnail_b64' && typeof v === 'string' ? `b64:${v.length}` : v,
        );
        const now = Date.now();
        const prev = seenMessages.get(key);
        if (prev !== undefined && now - prev < 10_000) return;
        seenMessages.set(key, now);
        if (seenMessages.size > 200) {
          for (const [k, t] of seenMessages) {
            if (now - t > 10_000) seenMessages.delete(k);
          }
        }
      }
      onInboundInbox(evt);
      onInboundTicket(evt);
    };

    // Marca msg como deletada no cache — emitido em 2 cenários:
    //  - cliente revogou via WhatsApp (worker-whatsmeow detecta REVOKE)
    //  - atendente clicou em Excluir (POST /messages/:id/revoke)
    const onMessageDeleted = (evt: {
      ticket_id: number;
      message_id: number;
      deleted_by: 'contact' | 'agent';
      deleted_at: string;
    }): void => {
      qc.setQueryData<InfiniteData<MessagesPage>>(['ticket-messages', evt.ticket_id], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            messages: p.messages.map((m) =>
              m.id === evt.message_id
                ? { ...m, deleted_at: evt.deleted_at, deleted_by: evt.deleted_by }
                : m,
            ),
          })),
        };
      });
      // Refresh do preview no card da inbox (last_message_preview pode ter
      // sido essa msg).
      void debugInvalidate(qc, ['tickets'], 'onMessageDeleted', 'message.deleted');
    };

    // Pacote do agrupador descartado antes de sair (POST /messages/:id/descartar):
    // a mensagem nunca foi pro cliente — some da conversa em todas as abas.
    const onMessageDiscarded = (evt: { ticket_id: number; message_id: number }): void => {
      qc.setQueryData<InfiniteData<MessagesPage>>(['ticket-messages', evt.ticket_id], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            messages: p.messages.filter((m) => m.id !== evt.message_id),
          })),
        };
      });
      // A prévia do card da lista podia ser justamente esse texto.
      void debugInvalidate(qc, ['tickets'], 'onMessageDiscarded', 'message.discarded');
    };

    // Cliente editou uma msg — atualiza body novo, preserva original_body.
    // Worker-whatsmeow detecta protocolMessage MESSAGE_EDIT e emite isto.
    const onMessageEdited = (evt: {
      ticket_id: number;
      message_id: number;
      new_body: string;
      edited_at: string;
    }): void => {
      qc.setQueryData<InfiniteData<MessagesPage>>(['ticket-messages', evt.ticket_id], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            messages: p.messages.map((m) => {
              if (m.id !== evt.message_id) return m;
              // Preserva original_body se ainda não tinha (1ª edição) — caso
              // contrário, mantém o snapshot original.
              const original = m.original_body ?? m.body;
              return {
                ...m,
                body: evt.new_body,
                edited_at: evt.edited_at,
                original_body: original,
              };
            }),
          })),
        };
      });
      void debugInvalidate(qc, ['tickets'], 'onMessageEdited', 'message.edited');
    };

    const onMessageReaction = (evt: {
      ticket_id: number;
      message_id: number;
      reaction: string | null;
      reaction_updated_at: string;
    }): void => {
      qc.setQueryData<InfiniteData<MessagesPage>>(['ticket-messages', evt.ticket_id], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            messages: p.messages.map((m) =>
              m.id === evt.message_id
                ? { ...m, reaction: evt.reaction, reaction_updated_at: evt.reaction_updated_at }
                : m,
            ),
          })),
        };
      });
    };

    // Wrappers nomeados: passam o nome do evento pro handler (um handler atende
    // vários eventos) pra atribuição correta no `__inboxStats` (correção #1 da
    // revisão). Mesma referência no `on` e no `off` — trocar só um vazaria o
    // listener.
    const onTicketQueued = (evt?: TicketLifecycleEvent): void =>
      onTicketChanged(evt, 'ticket.queued');
    // Mesmo tratamento do "assumido por outro" (onAssignedOrTransferred): se o
    // atendimento ABERTO na minha tela foi encerrado por OUTRA pessoa ou pela
    // automação (ex.: estava só visualizando um pendente e um colega encerrou),
    // a conversa sai da tela com aviso. Encerrado por mim não mexe: nesta aba o
    // ThreadPanel já fecha sozinho; noutra aba minha, segue aberto como fechado.
    const onTicketClosed = (evt?: TicketLifecycleEvent): void => {
      onTicketChanged(evt, 'ticket.closed');
      const afetaAberto =
        openTicketId !== null &&
        (evt?.ticket_id === openTicketId || (evt?.ticket_ids?.includes(openTicketId) ?? false));
      if (!afetaAberto) return;
      const me = useAuthStore.getState().user;
      if (!me) return;
      const actor = evt?.actor_user_id ?? null;
      if (actor === me.id) return;
      takenAwayRef.current?.();
      const card = evt?.ticket ?? evt?.tickets?.find((c) => c.id === openTicketId);
      const nome = nomeDoUsuario(qc, actor, card);
      toast.info(
        actor == null
          ? 'Este atendimento foi encerrado automaticamente.'
          : nome
            ? `Este atendimento foi encerrado por ${nome}.`
            : 'Este atendimento foi encerrado por outro atendente.',
      );
    };
    const onTicketReopened = (evt?: TicketLifecycleEvent): void =>
      onTicketChanged(evt, 'ticket.reopened');
    const onTicketTagged = (evt?: TicketLifecycleEvent): void =>
      onTicketChanged(evt, 'ticket.tagged');
    // Fim do bot NÃO passa mais pelo `onTicketChanged` — vide
    // `onChatbotEndedPatch`, que remenda o único campo que muda.
    const onChatbotEnded = onChatbotEndedPatch;
    const onTicketTransferred = (evt?: TicketLifecycleEvent): void =>
      onAssignedOrTransferred(evt, 'ticket.transferred');
    const onTicketAssigned = (evt?: TicketLifecycleEvent): void =>
      onAssignedOrTransferred(evt, 'ticket.assigned');
    const onContactUpdatedEvt = (): void => onContactUpdated('contact.updated');
    // Aviso com o link pronto (canal QR, desde 03/nov): troca só a foto nos
    // cartões e no cabeçalho daquele contato — nenhuma lista é rebuscada.
    // Aviso sem o link (worker antigo, Instagram) cai no caminho de sempre.
    const onContactProfilePicUpdated = (evt?: {
      contact_id?: number;
      profile_pic_url?: string | null;
    }): void => {
      if (evt && typeof evt.contact_id === 'number' && 'profile_pic_url' in evt) {
        trocaFotoDoContato(qc, evt.contact_id, evt.profile_pic_url ?? null);
        recordEncaixe('trocaFotoDoContato', 'contact.profile_pic.updated');
        qc.invalidateQueries({ queryKey: ['contacts'] });
        return;
      }
      onContactUpdated('contact.profile_pic.updated');
    };

    // Sinaliza pro useDesktopNotifications (global) que a página está montada
    // e os listeners daqui cobrem a thread — a defesa global de invalidar
    // ticket-messages só precisa agir quando é a ÚNICA cobertura (lição #70).
    inboxListeners.active += 1;

    socket.on('message.received', onMessageReceived);
    socket.on('message.status.updated', onStatus);
    socket.on('message.media.ready', onMediaReady);
    socket.on('message.deleted', onMessageDeleted);
    socket.on('message.discarded', onMessageDiscarded);
    socket.on('message.edited', onMessageEdited);
    socket.on('message.reaction.updated', onMessageReaction);
    socket.on('ticket.queued', onTicketQueued);
    socket.on('ticket.transferred', onTicketTransferred);
    socket.on('ticket.closed', onTicketClosed);
    socket.on('ticket.reopened', onTicketReopened);
    socket.on('ticket.tagged', onTicketTagged);
    socket.on('ticket.read', onTicketRead);
    socket.on('ticket.unread', onTicketUnread);
    socket.on('ticket.transferred.cleared', onTransferredCleared);
    // Aceitar ticket marca executions de chatbot como abandoned — invalida
    // pra remover ícone Bot do card.
    socket.on('ticket.assigned', onTicketAssigned);
    // Fluxo do chatbot encerrou (nó end / sem saída) → execução deixa de ser
    // 'running' → invalida a lista pra tirar o ícone de bot do card na hora.
    socket.on('chatbot.ended', onChatbotEnded);
    // Chatbot iniciou → acende ícone de bot no card na hora (execução já criada).
    socket.on('chatbot.started', onChatbotStarted);
    socket.on('contact.updated', onContactUpdatedEvt);
    socket.on('channel.updated', onChannelUpdated);
    // Worker-whatsmeow emite `contact.profile_pic.updated` quando detecta
    // mudança de foto de perfil. Mesma invalidação do contact.updated:
    // tickets/contacts/ticket aberto refetcham e o backend gera signed URL
    // fresca pro novo profile_pic_key.
    socket.on('contact.profile_pic.updated', onContactProfilePicUpdated);

    return () => {
      inboxListeners.active -= 1;
      socket.off('message.received', onMessageReceived);
      socket.off('message.status.updated', onStatus);
      socket.off('message.media.ready', onMediaReady);
      socket.off('message.deleted', onMessageDeleted);
      socket.off('message.discarded', onMessageDiscarded);
      socket.off('message.edited', onMessageEdited);
      socket.off('message.reaction.updated', onMessageReaction);
      socket.off('ticket.queued', onTicketQueued);
      socket.off('ticket.transferred', onTicketTransferred);
      socket.off('ticket.closed', onTicketClosed);
      socket.off('ticket.assigned', onTicketAssigned);
      socket.off('chatbot.ended', onChatbotEnded);
      socket.off('chatbot.started', onChatbotStarted);
      socket.off('ticket.reopened', onTicketReopened);
      socket.off('ticket.tagged', onTicketTagged);
      socket.off('ticket.read', onTicketRead);
      socket.off('ticket.unread', onTicketUnread);
      socket.off('ticket.transferred.cleared', onTransferredCleared);
      socket.off('contact.updated', onContactUpdatedEvt);
      socket.off('contact.profile_pic.updated', onContactProfilePicUpdated);
      socket.off('channel.updated', onChannelUpdated);
    };
  }, [qc, activeStatus, openTicketId]);
}

// Movida pra `card-do-evento.ts` (junto do encaixe do card); re-export mantém
// os consumidores existentes (ThreadPanel) sem mudança.
export { mediaPreviewForType } from './card-do-evento';

/** Helper UI: contagem total de não lidos por status. Retorna mapa {pending,active,closed} */
export function useUnreadCounts(tickets: TicketSummary[] | undefined): number {
  if (!tickets) return 0;
  return tickets.reduce((acc, t) => acc + t.unread_count, 0);
}
